import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

// ─── Módulo Insights & Análises ───────────────────────────────────────────────

type Rede = "instagram" | "tiktok" | "youtube" | "linkedin" | "twitter";

const REDE_INFO: Record<Rede, { label: string; icon: string }> = {
  instagram: { label: "Instagram", icon: "" },
  tiktok:    { label: "TikTok",    icon: "" },
  youtube:   { label: "YouTube",   icon: "▶️" },
  linkedin:  { label: "LinkedIn",  icon: "" },
  twitter:   { label: "Twitter/X", icon: "𝕏" },
};

const IMPACTO_INFO: Record<string, { label: string; color: string; bg: string }> = {
  alto:  { label: "Alto impacto",  color: "#10B981", bg: "#D1FAE5" },
  medio: { label: "Médio impacto", color: "#52525B", bg: "#FEF3C7" },
  baixo: { label: "Baixo impacto", color: "#9CA3AF", bg: "#F3F4F6" },
};

const LINHAS_EDITORIAIS = [
  { value: "L1 — Compliance & Controle",       color: "#0A0A0A", bg: "#EDEBE6" },
  { value: "L2 — Orquestração sem Milagre",    color: "#2563EB", bg: "#DBEAFE" },
  { value: "L3 — Dinheiro na Mesa",            color: "#10B981", bg: "#D1FAE5" },
  { value: "L4 — Bastidores da Operação",      color: "#52525B", bg: "#FEF3C7" },
  { value: "L5 — Maturidade e Visão",          color: "#8B5CF6", bg: "#EDE9FE" },
  { value: "L6 — Tendências e Provocações",    color: "#EC4899", bg: "#FDE8F5" },
];

const INPUT_CLS = "w-full border border-[#E7E5E0] rounded-lg px-3 py-2.5 text-sm text-[#0A0A0A] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#D6D3CD] focus:border-[#0A0A0A] transition bg-white";

interface Insight {
  id: number;
  clientId: number;
  titulo: string;
  descricao?: string | null;
  impacto: string;
  linhaEditorial?: string | null;
  rede?: string | null;
  formato?: string | null;
  origem: string;
  createdAt: number;
  updatedAt: number;
}

// ─── Modal manual ─────────────────────────────────────────────────────────────

function NovoInsightModal({
  clientId, insight, onSave, onClose,
}: {
  clientId: number;
  insight?: Insight | null;
  onSave: () => void;
  onClose: () => void;
}) {
  const [titulo, setTitulo]               = useState(insight?.titulo ?? "");
  const [descricao, setDescricao]         = useState(insight?.descricao ?? "");
  const [impacto, setImpacto]             = useState(insight?.impacto ?? "medio");
  const [linhaEditorial, setLinhaEditorial] = useState(insight?.linhaEditorial ?? "");
  const [rede, setRede]                   = useState<Rede | "">((insight?.rede as Rede) ?? "");
  const [formato, setFormato]             = useState(insight?.formato ?? "");

  const createMutation = trpc.insights.create.useMutation({ onSuccess: () => { onSave(); onClose(); } });
  const updateMutation = trpc.insights.update.useMutation({ onSuccess: () => { onSave(); onClose(); } });
  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleSave() {
    if (!titulo.trim() || isPending) return;
    if (insight) {
      updateMutation.mutate({
        id: insight.id, titulo: titulo.trim(), descricao: descricao.trim() || null,
        impacto: impacto as any, linhaEditorial: linhaEditorial.trim() || null,
        rede: rede || null, formato: formato.trim() || null,
      });
    } else {
      createMutation.mutate({
        clientId, titulo: titulo.trim(), descricao: descricao.trim() || undefined,
        impacto: impacto as any, linhaEditorial: linhaEditorial.trim() || undefined,
        rede: rede || undefined, formato: formato.trim() || undefined,
      });
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-12 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ border: "1px solid #E7E5E0" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <h2 className="font-bold text-base text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
            {insight ? "Editar insight" : "Novo insight"}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Título *</label>
            <input autoFocus type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
              placeholder="O aprendizado em uma frase" className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
              placeholder="O que funciona, o que não, e por quê — com a recomendação" rows={4}
              className={INPUT_CLS + " resize-none"} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Impacto</label>
            <div className="flex gap-2">
              {(["alto", "medio", "baixo"] as const).map(im => {
                const info = IMPACTO_INFO[im];
                const isActive = impacto === im;
                return (
                  <button key={im} type="button" onClick={() => setImpacto(im)}
                    className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{ background: isActive ? info.bg : "white", borderColor: isActive ? info.color : "#E7E5E0", color: isActive ? info.color : "#888" }}>
                    {info.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Linha editorial</label>
            <div className="flex flex-wrap gap-2">
              {LINHAS_EDITORIAIS.map(l => {
                const isActive = linhaEditorial.startsWith(l.value.split(" ")[0]);
                return (
                  <button key={l.value} type="button" onClick={() => setLinhaEditorial(isActive ? "" : l.value)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{ background: isActive ? l.bg : "white", borderColor: isActive ? l.color : "#E7E5E0", color: isActive ? l.color : "#888" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                    {l.value}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Rede</label>
              <select value={rede} onChange={e => setRede(e.target.value as Rede | "")} className={INPUT_CLS}>
                <option value="">—</option>
                {(Object.keys(REDE_INFO) as Rede[]).map(r => <option key={r} value={r}>{REDE_INFO[r].label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Formato</label>
              <input type="text" value={formato} onChange={e => setFormato(e.target.value)}
                placeholder="Reels, Carrossel…" className={INPUT_CLS} />
            </div>
          </div>
        </div>
        {(createMutation.error || updateMutation.error) && (
          <p className="px-6 py-2 text-red-500 text-sm">{(createMutation.error || updateMutation.error)?.message}</p>
        )}
        <div className="flex gap-3 px-6 py-4 shrink-0" style={{ borderTop: "1px solid #E7E5E0" }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F4F1] transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={!titulo.trim() || isPending}
            className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}>
            {isPending ? "Salvando…" : insight ? "Salvar" : "Criar insight"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Modal: gerar via Reportei + IA ───────────────────────────────────────────

function GerarInsightsModal({
  clientId, onSaved, onClose,
}: {
  clientId: number;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [rede, setRede] = useState<"instagram" | "linkedin">("instagram");
  const [dias, setDias] = useState(30);
  const [sugestoes, setSugestoes] = useState<{ titulo: string; descricao: string; impacto: string; formato: string | null }[]>([]);
  const [adicionadas, setAdicionadas] = useState<Set<number>>(new Set());

  const gerarMutation  = trpc.insights.gerarComIA.useMutation({
    onSuccess: (res: any) => { setSugestoes(res ?? []); setAdicionadas(new Set()); },
  });
  const createMutation = trpc.insights.create.useMutation();

  async function adicionar(idx: number) {
    const s = sugestoes[idx];
    if (!s || adicionadas.has(idx)) return;
    await createMutation.mutateAsync({
      clientId, titulo: s.titulo, descricao: s.descricao || undefined,
      impacto: s.impacto as any, rede, formato: s.formato || undefined, origem: "ia",
    });
    setAdicionadas(prev => new Set(prev).add(idx));
    onSaved();
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-12 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ border: "1px solid #E7E5E0" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <h2 className="font-bold text-base text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: "Poppins, sans-serif" }}>
            <span></span> Gerar insights via Reportei
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <p className="text-xs text-zinc-500">A IA analisa o desempenho real (Reportei) e gera aprendizados acionáveis.</p>
          <div className="flex gap-2">
            <div className="flex items-center gap-1 bg-[#F5F4F1] rounded-lg p-1 flex-1">
              {(["instagram", "linkedin"] as const).map(r => (
                <button key={r} onClick={() => setRede(r)}
                  className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
                  style={{ background: rede === r ? "white" : "transparent", color: rede === r ? "#0A0A0A" : "#888" }}>
                  {r === "instagram" ? "Instagram" : "LinkedIn"}
                </button>
              ))}
            </div>
            <select value={dias} onChange={e => setDias(Number(e.target.value))}
              className="text-xs border border-[#E7E5E0] rounded-lg px-2 py-1.5 bg-white">
              <option value={30}>30 dias</option>
              <option value={60}>60 dias</option>
              <option value={90}>90 dias</option>
            </select>
          </div>
          <button onClick={() => { setSugestoes([]); gerarMutation.mutate({ rede, dias }); }} disabled={gerarMutation.isPending}
            className="w-full py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-50"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}>
            {gerarMutation.isPending ? "Analisando dados…" : sugestoes.length > 0 ? "Gerar novamente" : "Analisar e gerar insights"}
          </button>
          {gerarMutation.error && <p className="text-xs text-red-500">{gerarMutation.error.message}</p>}

          {sugestoes.map((s, idx) => {
            const info = IMPACTO_INFO[s.impacto] ?? IMPACTO_INFO.medio;
            return (
              <div key={idx} className="rounded-xl border border-[#E7E5E0] p-3 bg-[#FAFAFE]">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>{s.titulo}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0" style={{ background: info.bg, color: info.color }}>
                    {info.label}
                  </span>
                </div>
                {s.descricao && <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{s.descricao}</p>}
                {s.formato && <p className="text-[11px] text-[#0A0A0A] mt-1">Formato: {s.formato}</p>}
                <button onClick={() => adicionar(idx)} disabled={adicionadas.has(idx)}
                  className="mt-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all disabled:opacity-60"
                  style={{ borderColor: adicionadas.has(idx) ? "#10B981" : "#0A0A0A", color: adicionadas.has(idx) ? "#10B981" : "#0A0A0A" }}>
                  {adicionadas.has(idx) ? "✓ Adicionado" : "+ Salvar no repositório"}
                </button>
              </div>
            );
          })}
        </div>
        <div className="px-6 py-4 shrink-0" style={{ borderTop: "1px solid #E7E5E0" }}>
          <button onClick={onClose} className="w-full py-2.5 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F4F1] transition-colors">
            Concluir
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export default function Insights() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;

  const [modalOpen, setModalOpen]     = useState(false);
  const [gerarOpen, setGerarOpen]     = useState(false);
  const [editando, setEditando]       = useState<Insight | null>(null);
  const [filtroImpacto, setFiltroImpacto] = useState<string>("todos");

  const { data: insights = [], isLoading, refetch } = trpc.insights.list.useQuery(
    { clientId }, { enabled: !!clientId }
  );
  const deleteMutation = trpc.insights.delete.useMutation({ onSuccess: () => refetch() });

  const filtrados = useMemo(() => {
    if (filtroImpacto === "todos") return insights;
    return insights.filter(i => i.impacto === filtroImpacto);
  }, [insights, filtroImpacto]);

  const cont = useMemo(() => {
    const c: Record<string, number> = { alto: 0, medio: 0, baixo: 0 };
    for (const i of insights) c[i.impacto] = (c[i.impacto] ?? 0) + 1;
    return c;
  }, [insights]);

  function handleDelete(id: number) {
    if (confirm("Excluir este insight?")) deleteMutation.mutate({ id });
  }

  return (
    <div className="h-full flex flex-col bg-[#F5F4F1]">
      {/* Header */}
      <div className="bg-white border-b border-[#E7E5E0] px-6 py-4 shrink-0">
        <div className="flex items-center gap-4 flex-wrap gap-y-3">
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-lg text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
              Insights & Análises
            </h1>
            <p className="text-zinc-400 text-xs mt-0.5">
              Aprendizados de performance — o que funciona, o que não, e por quê
            </p>
          </div>
          <button
            onClick={() => setGerarOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border-2 transition-all hover:bg-[#EDEBE6] shrink-0"
            style={{ borderColor: "#0A0A0A", color: "#0A0A0A", fontFamily: "Poppins, sans-serif" }}
          >
            Gerar via Reportei
          </button>
          <button
            onClick={() => { setEditando(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shrink-0"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Novo insight
          </button>
        </div>

        {/* KPIs / filtros */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <button onClick={() => setFiltroImpacto("todos")}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border"
            style={{
              background: filtroImpacto === "todos" ? "#0A0A0A" : "white",
              borderColor: filtroImpacto === "todos" ? "#0A0A0A" : "#E7E5E0",
              color: filtroImpacto === "todos" ? "white" : "#888",
            }}>
            Todos ({insights.length})
          </button>
          {(["alto", "medio", "baixo"] as const).map(im => {
            const info = IMPACTO_INFO[im];
            const isActive = filtroImpacto === im;
            return (
              <button key={im} onClick={() => setFiltroImpacto(im)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border-2"
                style={{ background: isActive ? info.bg : "white", borderColor: isActive ? info.color : "#E7E5E0", color: isActive ? info.color : "#888" }}>
                {info.label} ({cont[im] ?? 0})
              </button>
            );
          })}
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-[#E7E5E0] border-t-[#0A0A0A] animate-spin" />
        </div>
      ) : filtrados.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-zinc-400">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#EDEBE6" }}>
            <svg className="w-8 h-8 text-[#0A0A0A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <p className="text-sm font-medium">
            {filtroImpacto === "todos" ? "Nenhum insight ainda" : "Nenhum insight nesse impacto"}
          </p>
          {filtroImpacto === "todos" && (
            <button onClick={() => setGerarOpen(true)} className="text-sm font-semibold text-[#0A0A0A] underline underline-offset-2">
              Gerar insights via Reportei
            </button>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {filtrados.map(ins => {
              const info = IMPACTO_INFO[ins.impacto] ?? IMPACTO_INFO.medio;
              const lc = LINHAS_EDITORIAIS.find(l => ins.linhaEditorial?.startsWith(l.value.split(" ")[0]));
              return (
                <div key={ins.id} className="bg-white rounded-xl border border-[#E7E5E0] p-4 shadow-sm hover:shadow-md hover:border-[#D6D3CD] transition-all group">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[#0A0A0A] text-sm font-semibold leading-snug flex-1" style={{ fontFamily: "Poppins, sans-serif" }}>
                      {ins.titulo}
                    </p>
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold shrink-0" style={{ background: info.bg, color: info.color }}>
                      {info.label}
                    </span>
                  </div>
                  {ins.descricao && <p className="text-zinc-500 text-xs mt-2 line-clamp-4 leading-relaxed">{ins.descricao}</p>}
                  <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                    {lc && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: lc.bg, color: lc.color }}>
                        {ins.linhaEditorial}
                      </span>
                    )}
                    {ins.rede && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-[#F5F4F1] text-[#0A0A0A]">
                        {REDE_INFO[ins.rede as Rede]?.icon} {REDE_INFO[ins.rede as Rede]?.label}
                      </span>
                    )}
                    {ins.formato && <span className="text-[11px] text-zinc-400">{ins.formato}</span>}
                    {ins.origem === "ia" && <span className="text-[11px] text-zinc-400">IA</span>}
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-3 pt-3" style={{ borderTop: "1px solid #F5F4F1" }}>
                    <button onClick={() => { setEditando(ins as Insight); setModalOpen(true); }}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#0A0A0A] hover:bg-[#F5F4F1] transition-colors">
                      Editar
                    </button>
                    <button onClick={() => handleDelete(ins.id)}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-400 hover:bg-red-50 transition-colors">
                      Excluir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {modalOpen && (
        <NovoInsightModal
          clientId={clientId}
          insight={editando}
          onSave={() => refetch()}
          onClose={() => { setModalOpen(false); setEditando(null); }}
        />
      )}
      {gerarOpen && (
        <GerarInsightsModal
          clientId={clientId}
          onSaved={() => refetch()}
          onClose={() => setGerarOpen(false)}
        />
      )}
    </div>
  );
}
